import { css, cx } from '@linaria/core'
import { type FC, Fragment, type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '#components/Button'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { Modal } from '#components/Modal'
import { balanceEntries } from '#components/tabularOverlay/balanceEntries'
import { NotAvailable } from '#components/tabularOverlay/EntryHelpers'
import { type InfoGroup, joinInfoGroups } from '#components/tabularOverlay/InfoEntry'
import { TabHeader } from '#features/AppLayout'
import { toaster } from '#features/Toasts/toaster'
import CloseIcon from '#icons/close-16.svg'
import CopyIcon from '#icons/copy-outline-16.svg'
import { fragments, style } from '#styles'
import { theme } from '#styles/themes'
import { sharedTestIds } from '#utils/sharedTestIds'

const backdrop = css`
  // above other modals
  z-index: 10;
  flex-direction: column;
  justify-content: flex-start;
  
  > ._dialog {
    padding: 24px;
    width: 80vw;
    min-height: 60vh;
    max-height: 80vh;
    display: flex;
    margin-top: 10vh;
    flex-direction: column;
    gap: 24px;
    overflow-y: auto;
    
    > header {
      display: flex;
      justify-content: space-between;
      
      > h2 {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        gap: 4px;
        ${fragments.textStyle.heading.l.regular}
        
        & > .primary {
          ${fragments.textStyle.heading.l.regular}
        }

        & > .secondary {
          ${fragments.textStyle.heading.l.regular}
        }
        
        margin-right: 24px;
      }
      
      > .icon.accent {
        color: ${theme.textAccent};
      }
    }
    
    > ._tabs {
      display: flex;
      flex-direction: row;
      position: relative;
      gap: 8px;
      width: 100%;

      &:before {
        content: ' ';
        position: absolute;
        bottom: 0;
        width: 100%;
        display: block;
        border-bottom: 2px solid ${style.theme.borderSurface};
      }
    }
    
    > main {
      flex: 1;
      position: relative;
      
      display: flex;
      flex-direction: row;
      gap: 24px;

      &._default-tab * {
        user-select: text;
      }
      
      > div.column {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-column-gap: 15px;
        grid-row-gap: 31px;
        flex: 1;
      
        > section {
          display: grid;
          grid-template-columns: subgrid;
          grid-column: 1 / 3;
          grid-row-gap: 6px;  
          height: min-content;
          
          > h3 {
            grid-column: 1 / -1;
          }
          
          > div {
            max-width: 250px;
            overflow-x: hidden;
            display: flex;
            gap: 8px;
          }
        }
        
        &:not(:last-child) {
          border-right: 1px solid ${theme.borderSurface};
          padding-right: 24px;
        }
      }
    }
    
    > footer {
      display: flex;
      justify-content: flex-end;
      position: sticky;
      bottom: 0;
      gap: 12px;
      z-index: 1;
      background: ${style.theme.backgroundSurfacePrimaryStrong};
    }
  }
`

export const TabularInformationOverlay: FC<{
  className?: string
  children?: ReactNode
  title: ReactNode
  infoGroups: InfoGroup[]
  isOpen: boolean
  actions?: ReactNode
  setIsOpen: (isOpen: boolean) => void
  tabs?: { id: string; title: string; content?: ReactNode }[]
  'data-testid'?: string
}> = ({ className, children, infoGroups, title, actions, isOpen, setIsOpen, tabs, 'data-testid': dataTestId }) => {
  const { t } = useTranslation('shared')

  const balancedEntries = useMemo(() => {
    return balanceEntries(infoGroups)
  }, [infoGroups])

  const clipboardData: string = useMemo(() => {
    return joinInfoGroups(infoGroups, t)
  }, [infoGroups, t])

  const [activeTabId, setActiveTabId] = useState('default')
  const activeTabContent = tabs?.find((tab) => tab.id === activeTabId)?.content
  return (
    <Modal open={isOpen} lifted className={cx(backdrop, className)} onOpenChange={setIsOpen} data-testid={dataTestId}>
      <header>
        <h2>{title}</h2>
        <IconButton iconSize="m" className="icon accent" iconUrl={CloseIcon} title={t('common.button.close')} onClick={() => setIsOpen(false)} />
      </header>
      {tabs && (
        <div className="_tabs">
          {tabs.map((tab) => (
            <TabHeader
              key={tab.id}
              active={activeTabId === tab.id}
              onClick={() => setActiveTabId(tab.id)}
              role="tab"
              data-testid={sharedTestIds.systemInfo.tab(tab.id)}
            >
              {tab.title}
            </TabHeader>
          ))}
        </div>
      )}
      {activeTabId === 'default' && (
        <>
          <main className="_content _tabular-information _default-tab">
            {children}

            {infoGroups.length > 0 &&
              balancedEntries.map((group, columnIndex) => {
                return (
                  <div className="column" key={columnIndex}>
                    {group.map(({ groupId, entries, title }) => {
                      return (
                        <section key={groupId}>
                          <h3>{title}</h3>
                          {entries.map(({ title, entryId, value, displayValue }) => {
                            return (
                              <Fragment key={entryId}>
                                <div data-testid={sharedTestIds.systemInfo.entryByTitle}>{title}</div>
                                <div data-testid={sharedTestIds.systemInfo.entryByValue}>{displayValue ?? value ?? <NotAvailable />}</div>
                              </Fragment>
                            )
                          })}
                        </section>
                      )
                    })}
                  </div>
                )
              })}
          </main>

          <footer>
            {actions}
            {clipboardData.length > 0 && (
              <Button
                kind="secondary"
                size="m"
                className="flex"
                onClick={() => {
                  void navigator.clipboard.writeText(clipboardData)
                  toaster.add({
                    title: t('common.toasts.copied-to-clipboard', 'Copied to clipboard'),
                    'data-testid': sharedTestIds.systemInfo.copyToClipboardToast,
                  })
                }}
                data-testid={sharedTestIds.systemInfo.copyToClipboardButton}
              >
                <span>{t('common.button.copy-to-clipboard')}</span>
                <Icon iconUrl={CopyIcon} size="s" />
              </Button>
            )}
          </footer>
        </>
      )}
      {activeTabContent}
    </Modal>
  )
}
